// Initial glossary fallback (177 terms). The canonical seed is shared with the server in contracts.
import { match, norm } from '../lib/util';
import { GLOSSARY_SEED_DATA as data } from '@aiax-hub/contracts';
import type { GlossarySeedCategory as GlossaryCategory, GlossarySeedTerm as GlossaryTerm } from '@aiax-hub/contracts';
export type { GlossarySeedText as Bilingual, GlossarySeedCategory as GlossaryCategory, GlossarySeedCategoryId as GlossaryCategoryId, GlossarySeedTerm as GlossaryTerm } from '@aiax-hub/contracts';

export const GLOSSARY_CATEGORIES = data.categories as GlossaryCategory[];
export const GLOSSARY = data.terms as GlossaryTerm[];

const index = new Map<string, GlossaryTerm>();
for (const g of GLOSSARY) index.set(g.slug, g);
for (const g of GLOSSARY) for (const o of g.oldSlugs ?? []) if (!index.has(o)) index.set(o, g);

/** Term by slug (old slugs too — App.term). */
export const termOf = (slug: string | null | undefined): GlossaryTerm | null => (slug ? (index.get(slug) ?? null) : null);

/** Plain words in the language (falls back to the original term without its parenthesised part). */
export const plainOf = (g: GlossaryTerm, lang: 'ko' | 'en'): string => (lang === 'en' ? g.plainEn || g.termEn : g.plainKo || g.term.split(' (')[0] || g.term);

/** Language of the external page ('ko' wikipedia/docs or 'en'). */
export function docLangOf(g: GlossaryTerm): 'ko' | 'en' | null {
  if (!g.url) return null;
  return g.docLang || (/ko\.wikipedia|\/ko\/|\(한\)/.test(`${g.url} ${g.urlLabel ?? ''}`) ? 'ko' : 'en');
}

/** Search (command palette, glossary screen): plain words, original term, English, aliases, original names; 초성 works. */
export function searchTerms(q: string, limit = 6): GlossaryTerm[] {
  const s = q.trim();
  if (!s) return [];
  const nq = norm(s);
  const raw = (x: string) => norm(x).replace(/[_.\-·()（）,/'’‘"“”]+/g, '');
  return GLOSSARY.filter(
    (g) =>
      match(g.term, s) ||
      match(g.plainKo, s) ||
      match(g.plainEn, s) ||
      match(g.termEn, s) ||
      g.aliases.some((a) => match(a, s)) ||
      (g.rawNames ?? []).some((a) => raw(a).includes(nq.replace(/[_.\-·()（）,/'’‘"“”]+/g, ''))),
  ).slice(0, limit);
}

/** Suggestions when the search box is empty (palette). */
export const SUGGESTED_TERMS = ['token', 'estimated-cost', 'active-time', 'cache-read', 'recording-level'];
